import { cn } from "@repo/ui";

/** Set on `document.documentElement` by {@link SaasChromeVariables}. */
export const SAAS_APP_CHROME_HEIGHT_VAR = "--saas-app-chrome-height";
/** Horizontal inset for fixed layers that should align with the main column (below header). */
export const SAAS_APP_MAIN_LEFT_INSET_VAR = "--saas-app-main-left-inset";
export const SAAS_APP_MAIN_RIGHT_INSET_VAR = "--saas-app-main-right-inset";

/** Drop shadow under the SaaS app header bar (also used on the marketing site header). */
export const SAAS_APP_HEADER_BAR_SHADOW_CLASS =
	"shadow-[0_6px_18px_rgba(0,0,0,0.06)]";

/**
 * Same cast as {@link SAAS_APP_HEADER_BAR_SHADOW_CLASS}, as an inset shadow
 * (HUD drawer tip — shadow stays inside the arrow shape).
 */
export const SAAS_APP_HEADER_BAR_INSET_SHADOW_CLASS =
	"shadow-[inset_0_6px_18px_rgba(0,0,0,0.06)]";

/**
 * In-flow SaaS {@link HeaderBar} height (`h-12`). Used for confetti origins and
 * as the static fallback when `--saas-app-chrome-height` is not yet measured
 * (banner + header may make measured chrome taller).
 */
export const SAAS_APP_HEADER_HEIGHT_PX = 48;

/**
 * CSS length fallback for `var(--saas-app-chrome-height, …)` — matches
 * {@link SAAS_APP_HEADER_HEIGHT_PX} (3rem at default root font size).
 */
export const SAAS_APP_CHROME_HEIGHT_FALLBACK = "3rem";

/**
 * Shared frosted bar fill — chat composer footer (suggestions + input), panel
 * headers, collapsed learn topdrawer. Compose with layout classes as needed.
 */
export const SAAS_BLURRED_BAR_SURFACE_CLASS =
	"bg-background/55 backdrop-blur-md";

/**
 * Collapsed panel chrome height — learn topic topdrawer, chat headers,
 * platform-action headers (8px inset + size-8 circle = 48px).
 * Matches {@link SAAS_APP_HEADER_HEIGHT_PX}.
 */
export const SAAS_PANEL_HEADER_HEIGHT_PX = 48;
/** Inset padding inside {@link SAAS_PANEL_HEADER_HEIGHT_PX}. */
export const SAAS_PANEL_HEADER_INSET_PAD_PX = 8;

/**
 * Shared panel header row — matches collapsed {@link LearnTopicDetailTopDrawer}.
 * Includes {@link SAAS_BLURRED_BAR_SURFACE_CLASS}. Pair with
 * {@link SECONDARY_CTA_ICON_CIRCLE_CLASS} (`size-8`) controls.
 */
export const SAAS_PANEL_HEADER_CLASSNAME = cn(
	"box-border flex h-12 shrink-0 items-center overflow-visible border-b border-border/50 px-2 py-2",
	SAAS_BLURRED_BAR_SURFACE_CLASS,
);

/**
 * Header overlay tray shell (usage / jobs / notifications) — same palette as
 * UserMenu dropdown (`bg-popover` / `text-popover-foreground`).
 */
export const SAAS_HEADER_OVERLAY_TRAY_SURFACE_CLASS = cn(
	"rounded-2xl border border-border bg-popover text-popover-foreground",
	"shadow-[0_8px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.28)]",
);

/**
 * Shared max height for header overlay trays (usage / jobs / notifications).
 */
export const SAAS_HEADER_OVERLAY_TRAY_PANEL_MAX_H_CLASS =
	"max-h-[min(70vh,36rem)]";

/**
 * Min height aligned with the usage tray **above** the budget CTA bar
 * (header + meter tabs + hero/chart + history). Jobs / notifications / preferences
 * use this so trays share a consistent panel footprint.
 */
export const SAAS_HEADER_OVERLAY_TRAY_PANEL_MIN_H_CLASS =
	"min-h-[min(70vh,32rem)]";

/**
 * Exact desktop footprint matching {@link SAAS_HEADER_OVERLAY_TRAY_PANEL_MIN_H_CLASS}.
 * Prefer `h-full` so the tray fills the host-locked panel height (flex children /
 * footer can stretch to the bottom). `min-h` keeps sparse content (preferences)
 * measuring at the shared floor when the host reads `scrollHeight`.
 * Narrow: height is sheet-driven (`max-md:h-auto` + flex-1).
 */
export const SAAS_HEADER_OVERLAY_TRAY_PANEL_FLOOR_H_CLASS =
	"h-full min-h-[min(70vh,32rem)] max-md:h-auto max-md:min-h-0";

/**
 * Header overlay trays — same **48px** frost header as chat panel headers
 * ({@link SAAS_PANEL_HEADER_CLASSNAME} / {@link SAAS_BLURRED_BAR_SURFACE_CLASS}).
 */
export const SAAS_HEADER_OVERLAY_TRAY_HEADER_CLASSNAME = cn(
	"box-border flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border/50 px-3 sm:px-4",
	SAAS_BLURRED_BAR_SURFACE_CLASS,
);

/** Title in {@link SAAS_HEADER_OVERLAY_TRAY_HEADER_CLASSNAME}. */
export const SAAS_HEADER_OVERLAY_TRAY_TITLE_CLASSNAME = cn(
	"min-w-0 truncate text-sm font-black uppercase tracking-wide text-foreground sm:text-base",
);

/** Full-bleed hairline under tray sub-chrome (e.g. meter tabs) — no horizontal padding. */
export const SAAS_HEADER_OVERLAY_TRAY_DIVIDER_CLASS =
	"shrink-0 border-b border-border/50";

/**
 * Header overlay tray footer / entity bottom toolbar — same frost as
 * {@link SAAS_HEADER_OVERLAY_TRAY_HEADER_CLASSNAME} (border-t instead of border-b).
 * Pass as `className` on {@link EntityCollectionBottomToolbar} or action bars.
 * `!bg-background/55` beats entity-viewer narrow `bg-card` container chrome.
 */
export const SAAS_HEADER_OVERLAY_TRAY_FOOTER_CLASSNAME = cn(
	"box-border min-h-12 shrink-0 border-t border-border/50 px-3 py-2 sm:px-4",
	"!bg-background/55 backdrop-blur-md",
	"text-foreground",
);

/**
 * SaaS {@link HeaderBar} horizontal inset — same as panel header (`px-2`).
 */
export const SAAS_APP_HEADER_INLINE_PAD_CLASS = "px-2";

/**
 * Collapsed left-nav rail width: `px-2` (8) + 40px controls + `px-2` (8) = 56.
 */
export const SAAS_COLLAPSED_SIDEBAR_WIDTH_PX = 56;
/** CSS length for collapsed rail (overlays, chrome inset sync). */
export const SAAS_COLLAPSED_SIDEBAR_WIDTH_CSS = `${SAAS_COLLAPSED_SIDEBAR_WIDTH_PX}px`;

/**
 * Expanded left-nav rail width (`md:w-[280px]` on {@link NavBar}).
 */
export const SAAS_EXPANDED_SIDEBAR_WIDTH_PX = 280;
export const SAAS_EXPANDED_SIDEBAR_WIDTH_CSS = `${SAAS_EXPANDED_SIDEBAR_WIDTH_PX}px`;

/**
 * Collapsed left-nav control size (org select, nav items, roadmap, user menu).
 */
export const SAAS_COLLAPSED_NAV_CONTROL_SIZE_PX = 40;

/**
 * Collapsed left-nav icon center X from the viewport left edge:
 * `px-2` + half of 40px control → 8 + 20 = 28.
 */
export const SAAS_COLLAPSED_NAV_ICON_CENTER_X_PX = 28;

/**
 * Extra start gutter on the header logo so the **icon-only** logo center aligns
 * with {@link SAAS_COLLAPSED_NAV_ICON_CENTER_X_PX} when the header uses
 * {@link SAAS_APP_HEADER_INLINE_PAD_CLASS} (`8 + 4 + 16 = 28`).
 * Only at `md+` — below `md` the left nav is full-width (no icon rail), so left
 * inset matches right (`px-2` only).
 */
export const SAAS_HEADER_LOGO_GUTTER_START_CLASS = "md:pl-1";

/** Extra end gutter after the logo before header circle controls. */
export const SAAS_HEADER_LOGO_GUTTER_END_CLASS = "pr-3";

/**
 * Horizontal gap between adjacent {@link CircleButton}s in panel / header /
 * TopDrawer chrome clusters. Shared so glass rims don’t visually collapse spacing.
 */
export const SAAS_CIRCLE_BUTTON_CLUSTER_GAP_CLASS = "gap-2";

/** Account release notes (`/app/releases`) — full-bleed inside org-app `main`, not under the left nav. */
export function isAccountReleasesPathname(pathname: string): boolean {
	return /\/app\/releases(?:\/|$)/.test(pathname);
}

/**
 * Main-column horizontal insets for fixed overlays (completion shell, etc.).
 * Sidebars only consume in-flow space at `md+` (`SAAS_RIGHT_SIDEBAR_DESKTOP_MIN_WIDTH_PX`);
 * below that they are overlay sheets — insets must be `0px` or overlays look sidebar-padded.
 */
export function resolveSaasMainColumnInsets(input: {
	useSidebarLayout: boolean;
	isCollapsed: boolean;
	isRightSidebarOpen: boolean;
	isRightSidebarVisible: boolean;
	/** `true` when viewport matches Tailwind `md` / in-flow sidebar rails. */
	desktopRailLayout: boolean;
	rightSidebarDesktopWidthPx: number;
}): { left: string; right: string } {
	const left =
		input.useSidebarLayout && input.desktopRailLayout
			? input.isCollapsed
				? SAAS_COLLAPSED_SIDEBAR_WIDTH_CSS
				: SAAS_EXPANDED_SIDEBAR_WIDTH_CSS
			: "0px";
	const right =
		input.desktopRailLayout &&
		input.isRightSidebarOpen &&
		input.isRightSidebarVisible
			? `${input.rightSidebarDesktopWidthPx}px`
			: "0px";
	return { left, right };
}
